import { insensitive as i } from '@acme/common'
import dayjs from 'dayjs'
import localeText from 'locales/datagrid'
import ColumnEnum from './columEnum'
import { GridColDefPro } from './types'

export enum LinkOperator {
    And = 'and',
    Or = 'or',
}

export type GridFilterItemPro = {
    id?: number | string
    columnField: string
    value?: any
    operatorValue?: OperatorValue
    linkOperator?: LinkOperator
}

export interface GridFilterModelPro {
    items: GridFilterItemPro[]
    quickFilterValue?: string
}

export default class FilterModelPro {
    private _items: GridFilterModelPro['items']
    private _quickFilterValue: string
    public readonly columns: GridColDefPro[]

    /**
     * Se alguma das colunas utilizar valueGetter, é necessário passar as colunas.
     * @example new FilterModelPro(filterModel, columns)
     */
    constructor(filterModel?: GridFilterModelPro)
    constructor(filterModel: GridFilterModelPro, columns: Array<GridColDefPro>)
    constructor(
        filterModel?: GridFilterModelPro,
        columnsDef?: Array<GridColDefPro>,
    ) {
        this._quickFilterValue = filterModel?.quickFilterValue ?? ''
        this.columns = (columnsDef ?? []).filter(
            col => col.filterable !== false,
        )

        if (!filterModel || filterModel.items.length === 0) {
            this._items = []
            return
        }

        filterModel.items[0].linkOperator = LinkOperator.And
        for (const item of filterModel.items)
            if (!item.linkOperator) item.linkOperator = LinkOperator.And

        this._items = filterModel.items
    }

    get filter(): (row: any) => boolean {
        return (row: any) => {
            let result = this.doRegularFilter(row) && this.doQuickFilter(row)
            if ('children' in row) {
                const filteredChildren = row.children.filter(this.filter)
                result = result || filteredChildren.length > 0
            }
            return result
        }
    }

    private dt(str: string) {
        return dayjs(str?.toUpperCase(), [
            'DD/MM/YYYY HH:mm:ss',
            'DD/MM/YYYY HH:mm',
            'DD/MM/YYYY',
            'YYYY-MM-DD[T]HH:mm:ss',
            'YYYY-MM-DD[T]HH:mm',
            'YYYY-MM-DD HH:mm:ss',
            'YYYY-MM-DD',
            'HH:mm',
        ]).utc()
    }

    private doRegularFilter(row: any) {
        const itens = this.itens.filter(FilterModelPro.isItemComplete)
        if (!itens.length) return true

        const filterValues = itens.map(item => {
            let rawCellValue: string = row[item.columnField]

            if (this.columns) {
                const coluna = this.columns.find(
                    coluna => coluna.field === item.columnField,
                )
                if (coluna?.valueGetter)
                    rawCellValue = coluna.valueGetter({
                        row,
                        value: rawCellValue,
                        id: row.id,
                        field: coluna.field,
                    })
                if (coluna?.valueFormatter)
                    rawCellValue = coluna.valueFormatter({
                        value: rawCellValue,
                        id: row.id,
                        field: coluna.field,
                    })
            }

            rawCellValue = this.makeString(rawCellValue)

            const rawFilterValue: string = item.value?.toString() ?? ''
            const cellValue = rawCellValue.toLowerCase()
            const filterValue = rawFilterValue.toLowerCase()

            switch (item.operatorValue) {
                case 'contains':
                    return (
                        i(cellValue, 'lower').indexOf(i(filterValue, 'lower')) >
                        -1
                    )
                case 'equals':
                    return cellValue === filterValue
                case 'startsWith':
                    return cellValue.startsWith(filterValue)
                case 'endsWith':
                    return cellValue.endsWith(filterValue)
                case 'notContains':
                    return cellValue.indexOf(filterValue) === -1
                case 'notEquals':
                    return cellValue !== filterValue
                case 'isEmpty':
                    return cellValue === ''
                case 'isNotEmpty':
                    return cellValue !== ''
                // case 'isAnyOf':
                //     return filterValue.split(',').includes(cellValue)
                // case 'isNoneOf':
                //     return !filterValue.split(',').includes(cellValue)
                case '>':
                    return Number(cellValue) > Number(filterValue)
                case '<':
                    return Number(cellValue) < Number(filterValue)
                case '>=':
                    return Number(cellValue) >= Number(filterValue)
                case '<=':
                    return Number(cellValue) <= Number(filterValue)
                case '!=':
                    return Number(cellValue) !== Number(filterValue)
                case '=':
                    return Number(cellValue) === Number(filterValue)
                case 'is':
                    return this.dt(cellValue).isSame(this.dt(filterValue))
                case 'not':
                    return !this.dt(cellValue).isSame(this.dt(filterValue))
                case 'after':
                    return this.dt(cellValue).isAfter(this.dt(filterValue))
                case 'before':
                    return this.dt(cellValue).isBefore(this.dt(filterValue))
                case 'onOrAfter':
                    return this.dt(cellValue).isSameOrAfter(
                        this.dt(filterValue),
                    )
                case 'onOrBefore':
                    return this.dt(cellValue).isSameOrBefore(
                        this.dt(filterValue),
                    )
                default:
                    console.error(
                        `O operador ${item.operatorValue} não está ` +
                            `implementado em ${FilterModelPro.name}.`,
                    )
                    return true
            }
        })

        let value = filterValues[0]
        for (let i = 1; i < filterValues.length; i++) {
            if (itens[i].linkOperator === LinkOperator.And)
                value = value && filterValues[i]
            else value = value || filterValues[i]
        }

        return value
    }

    private doQuickFilter(row: any) {
        if (!this._quickFilterValue?.toString().trim()) return true

        const quickFilterValue = this.makeString(this._quickFilterValue)
            .trim()
            .toLowerCase()

        const filterValues = this.columns.map(coluna => {
            let value = row[coluna.field]
            if (coluna.valueGetter)
                value = coluna.valueGetter({
                    field: coluna.field,
                    id: row.id,
                    row,
                    value,
                })
            if (coluna.valueFormatter)
                value = coluna.valueFormatter({
                    field: coluna.field,
                    id: row.id,
                    value,
                })

            value = this.makeString(value).toLowerCase()

            return value.includes(quickFilterValue)
        })

        return !!filterValues.some(value => value)
    }

    private makeString(value?: any) {
        const strValue = structuredClone(value) ?? ''
        if (typeof strValue?.toString === 'function')
            value = strValue?.toString()
        else if (typeof strValue === 'object' && 'value' in strValue)
            value = String(strValue.value)
        else value = String(value)

        return value
    }

    get filterModel(): GridFilterModelPro {
        return {
            items: this.itens,
            quickFilterValue: this._quickFilterValue,
        }
    }

    get itens() {
        return this._items.map(item => {
            const operadoresSemValor = ['isEmpty', 'isNotEmpty']
            if (operadoresSemValor.includes(item.operatorValue ?? ''))
                return { ...item, value: '' }
            else return item
        })
    }

    public toUrlParams(): string {
        const itens = this.itens
            .filter(FilterModelPro.isItemComplete)
            .map(item => ({
                c: item.columnField,
                o: item.operatorValue,
                l: item.linkOperator,
                ...(item.value && {
                    v: item.value,
                }),
            }))
        return JSON.stringify(itens)
    }

    public static fromUrlParams(
        urlParams: string,
        columns: GridColDefPro[] = [],
    ): FilterModelPro {
        try {
            const items: GridFilterModelPro['items'] = JSON.parse(
                urlParams,
            ).map((i: any) => ({
                id: undefined,
                columnField: i.c,
                operatorValue: i.o,
                value: i.v ?? '',
                linkOperator: i.l || LinkOperator.And,
            }))
            for (const item of items) item.id = this.gerarID(items)
            return new FilterModelPro({ items }, columns)
        } catch (error) {
            console.error(error)
            return new FilterModelPro()
        }
    }

    public static gerarID(items: GridFilterModelPro['items']) {
        for (let i = 1; i <= items.length + 1; i++) {
            if (!items.find(item => item.id === i)) {
                return i
            }
        }
    }

    public static isItemComplete(item: GridFilterItemPro): boolean {
        if (!item.columnField || !item.operatorValue) return false

        const operadoresSemValor = ['isEmpty', 'isNotEmpty']
        if (operadoresSemValor.includes(item.operatorValue)) return true

        return (
            item.value !== undefined &&
            item.value !== null &&
            `${item.value}`.trim() !== ''
        )
    }

    public static getDefaultOperator(type?: GridColDefPro['type']) {
        if (ColumnEnum.isColumnEnum(type)) return 'equals'
        switch (type) {
            case 'time':
            case 'datetime-local':
            case 'date':
                return 'is'
            case 'SN':
            case 'boolean':
            case 'number':
                return 'equals'
            default:
                return 'contains'
        }
    }

    public static getOperators(type?: GridColDefPro['type']) {
        if (ColumnEnum.isColumnEnum(type)) return operadoresBooleanos
        switch (type) {
            case 'time':
            case 'datetime-local':
            case 'date':
                return operadoresTempo
            case 'number':
                return operadoresNumericos
            case 'SN':
            case 'boolean':
                return operadoresBooleanos
            default:
                return operadoresTexto
        }
    }
}

const operadoresTempo = [
    { value: 'is', label: localeText.filterOperatorIs },
    { value: 'not', label: localeText.filterOperatorNot },
    { value: 'after', label: localeText.filterOperatorAfter },
    { value: 'before', label: localeText.filterOperatorBefore },
    {
        value: 'onOrAfter',
        label: localeText.filterOperatorOnOrAfter,
    },
    {
        value: 'onOrBefore',
        label: localeText.filterOperatorOnOrBefore,
    },
    {
        value: 'isEmpty',
        label: localeText.filterOperatorIsEmpty,
    },
    {
        value: 'isNotEmpty',
        label: localeText.filterOperatorIsNotEmpty,
    },
] as const
type TimeOperatorValue = (typeof operadoresTempo)[number]['value']

const operadoresBooleanos = [
    { value: 'equals', label: localeText.filterOperatorEquals },
    { value: 'notEquals', label: localeText.filterOperatorNot },
    {
        value: 'isEmpty',
        label: localeText.filterOperatorIsEmpty,
    },
    {
        value: 'isNotEmpty',
        label: localeText.filterOperatorIsNotEmpty,
    },
] as const
type BooleanOperatorValue = (typeof operadoresBooleanos)[number]['value']

const operadoresNumericos = [
    { value: 'equals', label: localeText.filterOperatorEquals },
    { value: 'notEquals', label: localeText.filterOperatorNot },
    { value: '=', label: localeText.filterOperatorEquals },
    { value: '!=', label: localeText.filterOperatorNot },
    { value: '>', label: 'maior que' },
    { value: '>=', label: 'maior ou igual a' },
    { value: '<', label: 'menor que' },
    { value: '<=', label: 'menor ou igual a' },
    {
        value: 'isEmpty',
        label: localeText.filterOperatorIsEmpty,
    },
    {
        value: 'isNotEmpty',
        label: localeText.filterOperatorIsNotEmpty,
    },
] as const
type NumberOperatorValue = (typeof operadoresNumericos)[number]['value']

const operadoresTexto = [
    {
        value: 'contains',
        label: localeText.filterOperatorContains,
    },
    { value: 'notContains', label: 'não contém' },
    { value: 'equals', label: localeText.filterOperatorEquals },
    { value: 'notEquals', label: localeText.filterOperatorNot },
    {
        value: 'startsWith',
        label: localeText.filterOperatorStartsWith,
    },
    {
        value: 'endsWith',
        label: localeText.filterOperatorEndsWith,
    },
    {
        value: 'isEmpty',
        label: localeText.filterOperatorIsEmpty,
    },
    {
        value: 'isNotEmpty',
        label: localeText.filterOperatorIsNotEmpty,
    },
] as const
type TextOperatorValue = (typeof operadoresTexto)[number]['value']

export type OperatorValue =
    | TextOperatorValue
    | NumberOperatorValue
    | BooleanOperatorValue
    | TimeOperatorValue
